import React, { useMemo, useState, useEffect } from 'react';
import { Zap, AlertTriangle, Check, Play, Star, X, Eye, EyeOff, Sparkles } from 'lucide-react';
import { useLanguage } from '../services/languageContext';
import { useTranslatedText } from '../hooks/useTranslatedText';
import { TranslatedText } from './ui/TranslatedText';
import { ScrollReveal } from './ui/ScrollReveal';
import { Product, SiteConfig, HeroItem } from '../types';
import { normalizeVideoUrl, isEmbeddable } from '../services/videoUtils';
import { SaleTimer } from './ui/SaleTimer';
import { LazyVideo, LazyIframe } from './ui/LazyMedia';

interface HeroProps {
  onShopNow: () => void;
  products: Product[];
  siteConfig: SiteConfig;
  onProductClick: (p: Product) => void;
  onOpenMedia: (url: string, type: 'video' | 'image') => void;
}

export const Hero: React.FC<HeroProps> = ({ onShopNow, products, siteConfig, onProductClick, onOpenMedia }) => {
  const { t } = useLanguage();
  // Guarantee hero text is always visible — even when CSS animations are disabled
  // (e.g. iOS "Reduce Motion" leaves opacity:0 elements stuck invisible)
  const [heroVisible, setHeroVisible] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setHeroVisible(true), 50);
    return () => clearTimeout(id);
  }, []);

  const displayItems: HeroItem[] = siteConfig.heroVideos.length > 0 
    ? siteConfig.heroVideos 
    : [
        { title: "LIPSYNC V2", color: "bg-purple-500", delay: 0, url: '', type: 'video', isNSFW: false },
        { title: "DANCE PRO", color: "bg-blue-500", delay: 0.1, url: '', type: 'video', isNSFW: false },
        { title: "VIRAL REEL", color: "bg-green-500", delay: 0.2, url: '', type: 'video', isNSFW: false },
        { title: "CINEMATIC", color: "bg-red-500", delay: 0.3, url: '', type: 'video', isNSFW: false },
        { title: "GLOW FX", color: "bg-orange-500", delay: 0.4, url: '', type: 'video', isNSFW: false },
      ];

  // 🔥 SORTED Featured Products based on Config Order
  const featuredProducts = useMemo(() => {
    if (!siteConfig.featuredProductIds || siteConfig.featuredProductIds.length === 0) {
      return products.slice(0, 4); 
    }
    // Map IDs to products, maintaining ID array order
    return siteConfig.featuredProductIds
        .map(id => products.find(p => p.id === id))
        .filter((p): p is Product => !!p);
  }, [products, siteConfig.featuredProductIds]);

  return (
    <div className="w-full flex flex-col items-center overflow-x-hidden">
      
      {/* --- BLOCK 1: MAIN HERO (FULL SCREEN) --- */}
      <section className="relative w-full min-h-[100dvh] min-h-screen flex flex-col items-center justify-center px-3 sm:px-4 md:px-6 pt-16 pb-12 sm:pt-20 sm:pb-20">
        <div className="flex flex-col items-center text-center relative z-10 max-w-[1400px]">
          
          <h1 className="font-display font-black text-[clamp(1.75rem,8vw,4.5rem)] sm:text-5xl md:text-7xl lg:text-9xl leading-[0.92] md:leading-[0.85] tracking-tighter mb-5 sm:mb-6 md:mb-8 select-none uppercase drop-shadow-2xl flex flex-col items-center gap-0 px-1">
            <span 
              className={`block transition-all duration-700 ease-out ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
            >
              <span className="block bg-clip-text text-transparent bg-gradient-to-r from-[#2b0505] via-[#ef4444] to-[#2b0505] bg-[length:200%_auto] animate-shine px-2">
                <TranslatedText text={siteConfig.landingContent?.heroBlock?.titleTop} fallback={t('hero_title_top')} />
              </span>
            </span>

            <span 
              className={`block transition-all duration-700 ease-out delay-150 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
            >
              <span className="block bg-clip-text text-transparent bg-gradient-to-r from-gray-500 via-white to-gray-500 bg-[length:200%_auto] animate-shine px-2">
                <TranslatedText text={siteConfig.landingContent?.heroBlock?.titleBottom} fallback={t('hero_title_bottom')} />
              </span>
            </span>
          </h1>

          <h2 
            className={`text-xs sm:text-sm md:text-lg font-sans font-light text-white/40 max-w-2xl leading-relaxed mb-8 md:mb-12 tracking-wide transition-all duration-700 ease-out delay-300 px-6 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
          >
             <TranslatedText text={siteConfig.landingContent?.heroBlock?.subtitle} fallback={t('hero_title_2')} />
          </h2>

          <div 
            className={`flex flex-col sm:flex-row gap-4 sm:gap-5 mb-12 z-20 items-center transition-all duration-700 ease-out delay-500 w-full sm:w-auto px-6 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
          >
            <button 
              onClick={onShopNow}
              className="w-full sm:w-auto group relative px-8 py-4 md:px-10 rounded-full bg-white text-black font-display font-bold text-xs uppercase tracking-[0.2em] overflow-hidden transition-all duration-300 ease-out hover:scale-105 hover:shadow-[0_0_40px_rgba(255,255,255,0.4)]"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-gray-300/50 to-transparent -translate-x-full group-hover:animate-[shine_1s_ease-in-out]" />
              <span className="relative z-10"><TranslatedText text={siteConfig.landingContent?.heroBlock?.shopBtnLabel} fallback={t('hero_btn_shop')} /></span>
            </button>
            
            <a 
              href={(siteConfig.landingContent?.heroBlock?.contactBtnUrl || 'https://t.me/miranovui').trim() || 'https://t.me/miranovui'}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto px-8 py-4 md:px-10 rounded-full bg-white/5 border border-white/10 text-white/70 font-display font-bold text-xs uppercase tracking-[0.2em] backdrop-blur-md flex items-center justify-center transition-all duration-300 ease-out hover:bg-white/10 hover:text-white hover:border-white/30 hover:scale-105"
            >
               <TranslatedText text={siteConfig.landingContent?.heroBlock?.contactBtnLabel} fallback={t('hero_btn_contact')} />
            </a>
          </div>

        </div>
      </section>

      {/* --- BLOCK 1.5: CMS SHOWCASE — 2 cols on phone (4+ items = two columns), 5 cols desktop --- */}
      <section className="w-full max-w-[1600px] px-3 sm:px-4 md:px-6 mb-16 sm:mb-24 md:mb-32 -mt-12 sm:-mt-20 z-20 relative">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2.5 sm:gap-3 md:gap-4 py-4 sm:py-6 md:py-10 w-full max-w-[1600px] mx-auto">
             {displayItems.map((item, idx) => {
               const cleanUrl = item.type === 'video' ? normalizeVideoUrl(item.url) : item.url;
               const isDrive = item.type === 'video' ? isEmbeddable(cleanUrl || '') : false;
               const isImage = item.type === 'image';
               
               return (
               <ScrollReveal key={idx} delay={Math.min((item.delay || idx * 0.05) as number, 0.3)} className="w-full min-w-0">
                 <div 
                    onClick={() => cleanUrl && onOpenMedia(cleanUrl, item.type)}
                    className="group relative aspect-[9/16] w-full max-h-[min(52vh,420px)] sm:max-h-none mx-auto bg-[#0A0A0A] rounded-xl sm:rounded-2xl border border-white/10 overflow-hidden cursor-pointer active:scale-[0.98] sm:hover:border-white/30 transition-all duration-300 sm:duration-500 sm:hover:-translate-y-1 md:hover:-translate-y-4 shadow-xl sm:shadow-2xl"
                 >
                    <div className="absolute inset-0 bg-[#050505] overflow-hidden">
                        {item.isNSFW && (
                          <div className="absolute top-4 left-4 z-30 bg-red-600 text-white text-[8px] font-black uppercase tracking-widest px-2 py-1 rounded shadow-lg border border-red-400 flex items-center gap-1">
                             <EyeOff size={8} /> 18+
                          </div>
                        )}

                        {cleanUrl ? (
                          <>
                             <div className={`w-full h-full transition-all duration-500 ${item.isNSFW ? 'blur-xl sm:group-hover:blur-md scale-110' : 'opacity-80 sm:opacity-60 sm:group-hover:opacity-100'}`}>
                                {isImage ? (
                                    <img src={cleanUrl} alt={item.title} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                                ) : (
                                    isDrive ? (
                                        <div className="w-full h-full relative pointer-events-none min-h-[120px]">
                                            <LazyIframe
                                              eager
                                              src={cleanUrl}
                                              className="w-[300%] h-full min-h-[120px] -ml-[100%] object-cover border-0"
                                              title="video"
                                            />
                                        </div>
                                    ) : (
                                        <LazyVideo
                                          eager
                                          src={cleanUrl}
                                          autoPlay
                                          muted
                                          loop
                                          playsInline
                                          className="w-full h-full min-h-[120px] object-cover"
                                        />
                                    )
                                )}
                             </div>
                             {item.type === 'video' && isDrive && <div className="absolute inset-0 z-10" />}
                          </>
                        ) : (
                          <div className={`absolute bottom-0 left-0 right-0 h-1/2 bg-gradient-to-t from-${item.color ? item.color.replace('bg-', '') : 'purple-500'}/20 to-transparent opacity-50`} />
                        )}
                        
                        <div className="absolute top-4 right-4 w-2 h-2 rounded-full bg-red-500 animate-pulse shadow-[0_0_10px_red]" />
                        
                        <div className="absolute inset-0 flex items-center justify-center opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-300 pointer-events-none z-20">
                           <div className="w-11 h-11 sm:w-12 sm:h-12 bg-black/50 sm:bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center border border-white/30 sm:border-white/20 shadow-glass">
                              {item.type === 'image' ? <Eye size={18} className="text-white sm:w-5 sm:h-5"/> : <Play size={18} fill="white" className="text-white ml-0.5 sm:w-5 sm:h-5" />}
                           </div>
                        </div>

                        <div className="absolute bottom-2 left-2 right-2 sm:bottom-4 sm:left-4 sm:right-auto z-20 pointer-events-none">
                           <p className="text-[8px] sm:text-[10px] font-mono uppercase text-gray-400 mb-0.5 sm:mb-1 tracking-widest">Showcase</p>
                           <p className="text-[11px] sm:text-sm font-bold font-display text-white uppercase tracking-wider shadow-black drop-shadow-md line-clamp-2"><TranslatedText text={item.title} /></p>
                        </div>
                    </div>
                    
                    <div className="absolute inset-0 bg-[linear-gradient(rgba(18,16,16,0)_50%,rgba(0,0,0,0.25)_50%)] bg-[length:100%_4px] pointer-events-none opacity-20 z-10" />
                 </div>
               </ScrollReveal>
             );
             })}
          </div>
      </section>

      {/* --- BLOCK 2: PAIN VS SOLUTION --- */}
      <section className="w-full bg-[#030304] border-y border-white/10 py-16 md:py-24 z-20 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-indigo-500 to-transparent opacity-50" />
        <div className="max-w-6xl mx-auto px-6">
           <ScrollReveal className="text-center mb-12 md:mb-16">
               <h2 className="text-2xl md:text-5xl font-display font-black text-white uppercase leading-none mb-6">
                  <TranslatedText text={siteConfig.landingContent?.pain?.title} fallback={t('pain_title')} />
               </h2>
               <div className="w-24 h-1 bg-indigo-500 mx-auto rounded-full shadow-[0_0_15px_#6366f1]" />
           </ScrollReveal>
           <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              <ScrollReveal delay={0.1} className="p-6 md:p-10 rounded-2xl bg-[#080808] border border-red-900/30 relative overflow-hidden group hover:border-red-900/60 transition-colors">
                 <div className="absolute -right-10 -top-10 w-40 h-40 bg-red-900/20 blur-3xl rounded-full pointer-events-none" />
                 <div className="flex items-center gap-3 mb-6 md:mb-8">
                    <AlertTriangle className="text-red-500" size={24} />
                    <h3 className="text-sm font-mono text-red-400 uppercase tracking-widest font-bold"><TranslatedText text={siteConfig.landingContent?.pain?.problemTitle} fallback={t('pain_box_title_1')} /></h3>
                 </div>
                 <ul className="space-y-4 mb-8">
                    {((siteConfig.landingContent?.pain?.problems?.length ? siteConfig.landingContent.pain.problems : null) ?? [t('pain_p_1'), t('pain_p_2'), t('pain_p_3'), t('pain_p_4')]).map((text, i) => (
                      <li key={i} className="flex items-center gap-4 text-gray-400 font-light text-sm md:text-lg"><X size={18} className="text-red-500 shrink-0" /> <TranslatedText text={(text || '').trim() || undefined} fallback={t(`pain_p_${Math.min(i + 1, 4)}`)} /></li>
                    ))}
                 </ul>
                 <div className="w-full bg-white/5 h-1.5 rounded-full overflow-hidden">
                    <div className="h-full w-[20%] bg-red-600 rounded-full" />
                 </div>
              </ScrollReveal>
              <ScrollReveal delay={0.2} className="p-6 md:p-10 rounded-2xl bg-[#080808] border border-green-900/30 relative overflow-hidden group hover:border-green-500/30 transition-colors">
                 <div className="absolute -right-10 -top-10 w-40 h-40 bg-green-500/10 blur-3xl rounded-full pointer-events-none" />
                 <div className="flex items-center gap-3 mb-6 md:mb-8">
                    <Zap className="text-green-500" size={24} />
                    <h3 className="text-sm font-mono text-green-400 uppercase tracking-widest font-bold"><TranslatedText text={siteConfig.landingContent?.pain?.solutionTitle} fallback={t('pain_box_title_2')} /></h3>
                 </div>
                 <ul className="space-y-4 mb-8">
                    {((siteConfig.landingContent?.pain?.solutions?.length ? siteConfig.landingContent.pain.solutions : null) ?? [t('pain_s_1'), t('pain_s_2'), t('pain_s_3'), t('pain_s_4'), t('pain_s_5')]).map((text, i) => (
                      <li key={i} className="flex items-center gap-4 text-white font-medium text-sm md:text-lg"><Check size={18} className="text-green-500 shrink-0" /> <TranslatedText text={(text || '').trim() || undefined} fallback={t(`pain_s_${Math.min(i + 1, 5)}`)} /></li>
                    ))}
                 </ul>
                 <div className="w-full bg-white/5 h-1.5 rounded-full overflow-hidden">
                    <div className="h-full w-[100%] bg-green-500 rounded-full shadow-[0_0_15px_#22c55e]" />
                 </div>
              </ScrollReveal>
           </div>
        </div>
      </section>

      {/* --- BLOCK 3: CMS FEATURED PRODUCTS --- */}
      <section className="w-full py-16 md:py-24 max-w-[1600px] mx-auto px-6">
         <ScrollReveal className="flex flex-col md:flex-row justify-between items-start md:items-end mb-10 md:mb-16 gap-6">
            <h2 className="text-3xl md:text-6xl font-display font-black text-white uppercase">{t('feat_title')}</h2>
            <button onClick={onShopNow} className="flex items-center gap-2 text-xs font-bold uppercase bg-white/5 hover:bg-white/10 px-4 py-2 rounded-full border border-white/10 transition-colors">
               View All Assets <span className="group-hover:translate-x-1 transition-transform">→</span>
            </button>
         </ScrollReveal>
         
         {featuredProducts.length > 0 ? (
           <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
             {featuredProducts.map((p, idx) => (
                <FeatureCard 
                  key={p.id}
                  title={p.title} 
                  desc={p.categories[0]} 
                  price={p.price} 
                  originalPrice={p.originalPrice}
                  isOnSale={p.isOnSale}
                  saleEndsAt={p.saleEndsAt} // Pass timer prop
                  isNew={p.isNew}
                  img={p.coverImage}
                  onClick={() => onProductClick(p)}
                  color={`from-indigo-500/20`}
                  delay={idx * 0.1}
                  isHit={idx === 0 || idx === featuredProducts.length -1}
                />
             ))}
           </div>
         ) : (
           <div className="text-center py-20 text-gray-500">
             No featured assets configured.
           </div>
         )}
      </section>

      {/* --- BLOCK 4: SOCIAL PROOF --- */}
      <section className="w-full border-y border-white/10 bg-[#050505] relative">
        <div className="absolute inset-0 bg-noise opacity-[0.03]" />
        <ScrollReveal className="max-w-[1600px] mx-auto grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-white/10 relative z-10">
           {((siteConfig.landingContent?.stats?.length ? siteConfig.landingContent.stats : null) ?? [
             { number: '500+', label: t('proof_stat_1') },
             { number: '+280%', label: t('proof_stat_2'), highlight: true },
             { number: '4.9/5', label: t('proof_stat_3') }
           ]).map((s, i) => (
             <TranslatedStatBox key={i} number={(s.number || '').trim() || '-'} label={(s.label || '').trim() || t(`proof_stat_${(i % 3) + 1}`)} highlight={s.highlight} />
           ))}
        </ScrollReveal>
      </section>

      {/* --- BLOCK 5: FINAL CTA --- */}
      <section className="w-full py-24 md:py-40 px-4 md:px-6 relative overflow-hidden bg-[#020202]">
        <div className="absolute inset-0 bg-grid-pattern bg-[length:50px_50px] opacity-[0.05]" />
        <div className="absolute bottom-0 left-0 right-0 h-[400px] bg-gradient-to-t from-indigo-900/20 to-transparent pointer-events-none blur-3xl" />
        
        <ScrollReveal className="max-w-6xl mx-auto relative z-10">
           <div className="relative rounded-[2rem] overflow-hidden border border-white/10 bg-[#08080A]/60 backdrop-blur-3xl shadow-[0_0_100px_rgba(0,0,0,0.8)] group">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[60%] h-[1px] bg-gradient-to-r from-transparent via-indigo-500 to-transparent opacity-70" />
              <div className="absolute -top-[200px] left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-indigo-500/10 blur-[120px] rounded-full pointer-events-none" />

              <div className="px-6 py-16 md:p-24 flex flex-col items-center text-center">
                  <h2 className="text-3xl md:text-5xl font-display font-black text-white mb-6 md:mb-8 uppercase leading-none tracking-tight">
                     <TranslatedText text={siteConfig.landingContent?.cta?.title} fallback={t('cta_title')} />
                  </h2>
                  <p className="text-sm md:text-xl text-white/40 max-w-2xl mb-10 md:mb-12 font-light leading-relaxed">
                     <TranslatedText text={siteConfig.landingContent?.cta?.text} fallback={t('cta_text')} as="span" />
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 w-full justify-center items-center">
                     <button onClick={onShopNow} className="w-full sm:w-auto group relative px-12 py-5 rounded-full bg-white text-black min-w-[200px] font-display font-bold text-sm uppercase tracking-widest overflow-hidden transition-all duration-300 ease-out hover:scale-105 hover:shadow-[0_0_50px_rgba(255,255,255,0.3)]">
                        <span className="relative z-10">{t('hero_btn_shop')}</span>
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-gray-200 to-transparent opacity-50 -translate-x-full group-hover:animate-[shine_1s_ease-in-out]" />
                     </button>
                     <a href={(siteConfig.landingContent?.cta?.btnUrl || 'https://t.me/miranovui').trim() || 'https://t.me/miranovui'} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto px-12 py-5 rounded-full min-w-[200px] bg-[#229ED9]/10 text-[#229ED9] border border-[#229ED9]/30 font-display font-bold text-sm uppercase tracking-widest flex items-center justify-center hover:bg-[#229ED9] hover:text-white transition-all duration-300 hover:shadow-[0_0_30px_rgba(34,158,217,0.4)]">
                        <TranslatedText text={siteConfig.landingContent?.cta?.btnLabel} fallback={t('cta_btn_tg')} />
                     </a>
                  </div>
              </div>
           </div>
        </ScrollReveal>
      </section>

    </div>
  );
};

const StatBox = ({ number, label, highlight }: { number: string, label: string, highlight?: boolean }) => (
  <div className="py-12 md:py-16 flex flex-col items-center justify-center hover:bg-white/[0.02] transition-colors group">
      <span className={`text-4xl md:text-6xl font-display font-black mb-4 ${highlight ? 'text-transparent bg-clip-text bg-gradient-to-b from-green-400 to-green-600' : 'text-white'}`}>{number}</span>
      <span className="text-[10px] md:text-xs font-mono uppercase tracking-[0.3em] text-gray-500 group-hover:text-white transition-colors">{label}</span>
  </div>
);

const TranslatedStatBox = ({ number, label, highlight }: { number: string, label: string, highlight?: boolean }) => (
  <div className="py-12 md:py-16 flex flex-col items-center justify-center hover:bg-white/[0.02] transition-colors group">
      <span className={`text-4xl md:text-6xl font-display font-black mb-4 ${highlight ? 'text-transparent bg-clip-text bg-gradient-to-b from-green-400 to-green-600' : 'text-white'}`}>{number}</span>
      <span className="text-[10px] md:text-xs font-mono uppercase tracking-[0.3em] text-gray-500 group-hover:text-white transition-colors"><TranslatedText text={label || undefined} fallback={label} /></span>
  </div>
);

const FeatureCard = ({ title, desc, price, originalPrice, isOnSale, saleEndsAt, isNew, isHit, img, onClick, color, delay }: any) => {
  const { t } = useLanguage();
  const translatedTitle = useTranslatedText(title, 'ru');
  const translatedDesc = useTranslatedText(desc, 'ru');
  const isSaleValid = isOnSale && (!saleEndsAt || new Date(saleEndsAt) > new Date());

  return (
  <ScrollReveal delay={delay}>
    <div onClick={onClick} className="group relative aspect-[3/4] min-h-[200px] sm:min-h-[280px] sm:aspect-auto sm:h-[350px] md:h-[450px] bg-[#0A0A0A] border border-white/5 cursor-pointer overflow-hidden rounded-xl sm:rounded-2xl">
      <img src={img} alt={title} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-80 group-hover:scale-110 transition-all duration-700" />
      
      <div className={`absolute inset-0 bg-gradient-to-t from-black via-black/40 ${color} to-transparent opacity-80`} />
      
      <div className="absolute top-4 left-4 right-4 flex justify-between items-start z-20">
          <div className="flex flex-col gap-2 items-start">
             <span className="font-bold text-xs text-white uppercase tracking-widest border border-white/10 px-3 py-1.5 bg-black/40 backdrop-blur rounded-lg flex flex-col items-end gap-1">
                {isSaleValid && originalPrice ? (
                  <>
                    <span className="text-gray-400 line-through text-[10px] decoration-red-500/50">${originalPrice}</span>
                    <span className="text-green-400">${price}</span>
                  </>
                ) : (
                   <span>${price}</span>
                )}
             </span>
             {isSaleValid && saleEndsAt && <SaleTimer deadline={saleEndsAt} size="sm" />}
          </div>

          <div className="flex gap-2">
            {isSaleValid && (
                <span className="flex items-center gap-1 bg-green-500 text-black text-[10px] font-bold uppercase px-2 py-1 rounded shadow-lg">SALE</span>
            )}
            {isNew && (
                <span className="flex items-center gap-1 bg-cyan-500 text-black text-[10px] font-bold uppercase px-2 py-1 rounded shadow-lg"><Star size={10} fill="black" /> NEW</span>
            )}
            {isHit && (
                <span className="flex items-center gap-1 bg-white text-black text-[10px] font-bold uppercase px-2 py-1 rounded shadow-lg"><Star size={10} fill="black" /> Hit</span>
            )}
          </div>
      </div>

      <div className="absolute bottom-0 left-0 w-full p-3 sm:p-6 translate-y-0 sm:translate-y-2 sm:group-hover:translate-y-0 transition-transform duration-500 z-20">
          <h3 className="text-sm sm:text-xl md:text-2xl font-display font-bold text-white uppercase leading-tight mb-1 sm:mb-2 drop-shadow-md line-clamp-2">{translatedTitle}</h3>
          <p className="text-[10px] sm:text-xs text-gray-300 font-mono uppercase tracking-widest mb-2 sm:mb-6 opacity-80 line-clamp-1">{translatedDesc}</p>
          <div className="flex items-center gap-2 text-[10px] sm:text-xs font-bold uppercase tracking-widest text-white opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-all duration-500 sm:transform sm:translate-y-4 sm:group-hover:translate-y-0">
             {t('btn_to_shop')} <span className="sm:group-hover:translate-x-1 transition-transform">→</span>
          </div>
      </div>
    </div>
  </ScrollReveal>
)};